import React, { useEffect, useState } from "react";
import { fetchLoopFunnel } from "../services/analyticsService";
import AnalyticsSection from "./AnalyticsSection";
import PanelHeadline, { Fig, Delta } from "./PanelHeadline";

// The doors-open LOOP funnel (founder "build it for us", 2026-08-31):
// the weekly pulse of the core loop — introductions -> Vault -> reveal ->
// chat -> meetup — plus the exits, four rolling 7-day windows, newest
// first. Real members only; the signup wizard funnel lives further down
// the page. Until this existed, every "how is the loop doing" answer was
// a forensic dig.
const STAGES = [
  { key: "newMembers", label: "New members" },
  { key: "introductions", label: "Introductions created" },
  { key: "vaultsEntered", label: "Vaults entered (pairs)" },
  { key: "reveals", label: "Reveals completed" },
  { key: "firstMessages", label: "Chats with a first message" },
  { key: "twoWay", label: "…now two-way" },
  { key: "meetups", label: "Meetups confirmed" },
  { key: "endsPreReveal", label: "Ends before reveal", exit: true },
  { key: "closesPostReveal", label: "Closes after reveal", exit: true },
  { key: "deletions", label: "Account deletions", exit: true },
];

const PAIR_TYPES = [
  { key: "proven_proven", label: "Proven × Proven" },
  { key: "proven_untested", label: "Proven × Untested" },
  { key: "untested_untested", label: "Untested × Untested" },
  { key: "proven_ghost", label: "Proven × Ghost" },
  { key: "untested_ghost", label: "Untested × Ghost" },
  { key: "ghost_ghost", label: "Ghost × Ghost" },
  { key: "unknown", label: "Dealt before tiers existed" },
];

const fmtRange = (start, end) => {
  const opts = { month: "short", day: "numeric" };
  const s = new Date(start).toLocaleDateString("en-CA", opts);
  const e = new Date(end).toLocaleDateString("en-CA", opts);
  return `${s} – ${e}`;
};

const LoopFunnelStats = ({ startLoading, stopLoading }) => {
  const [weeks, setWeeks] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        startLoading();
        setFailed(false);
        const res = await fetchLoopFunnel();
        setWeeks(res?.data?.data?.weeks || []);
      } catch (err) {
        console.error("Failed to load loop funnel", err);
        setFailed(true);
      } finally {
        stopLoading();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AnalyticsSection
      id="loop"
      icon="mdi:swap-horizontal"
      title="The loop this week"
      basis="Introductions through to meetups. Real members only, test accounts excluded. Rolling 7-day weeks, newest first. This is the member loop, not the signup wizard at the foot of this page."
    >
      {/* The reading, before the evidence. Both figures appear in the table
          directly below, so the sentence can always be checked. */}
      <PanelHeadline
        failed={failed}
        failedText="Could not load the loop. The tables below are blank, not zero."
        empty={!weeks || !weeks.length}
        emptyText="No weeks recorded yet. Nothing has run through the loop."
      >
        <Fig>{weeks?.[0]?.reveals ?? 0}</Fig> of{" "}
        <Fig>{weeks?.[0]?.introductions ?? 0}</Fig> introductions reached a reveal
        this week
        <Delta
          now={weeks?.[0]?.reveals ?? 0}
          prev={weeks?.[1]?.reveals}
          label="last week"
        />
      </PanelHeadline>

      {failed && (
        <div className="alert alert-danger py-2">
          Could not load the loop funnel. Refresh to retry.
        </div>
      )}
      {weeks && weeks.length > 0 && (
        <div className="table-responsive">
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                <th>Stage</th>
                {weeks.map((w, idx) => (
                  <th key={idx} className="text-center">
                    {idx === 0 ? "This week" : fmtRange(w.start, w.end)}
                    {idx === 0 && (
                      <div className="text-xs text-secondary-light fw-normal">
                        {fmtRange(w.start, w.end)}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {STAGES.map((stage) => (
                <tr key={stage.key}>
                  <td className={stage.exit ? "text-secondary-light" : ""}>
                    {stage.label}
                  </td>
                  {weeks.map((w, idx) => (
                    <td
                      key={idx}
                      className={
                        "text-center" +
                        (stage.exit ? " text-secondary-light" : "")
                      }
                    >
                      {w[stage.key] ?? 0}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {/* By pair type (2026-09-04): show-up tiers stamped at dealing.
              "introductions / reveals" per type answers whether pairing
              people who show up with each other actually converts. Pairs
              dealt before the stamp existed read as unknown. */}
          <h6 className="text-md fw-semibold mt-24 mb-8">By pair type (dealt · revealed)</h6>
          <p className="text-sm text-secondary-light mb-12">
            Proven = finished a Vault or chose Reveal before · Untested = new · Ghost = served 2+, never answered
          </p>
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                <th>Pair type</th>
                {weeks.map((w, idx) => (
                  <th key={idx} className="text-center">
                    {idx === 0 ? "This week" : fmtRange(w.start, w.end)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PAIR_TYPES.map((t) => (
                <tr key={t.key}>
                  <td className={t.key === "unknown" ? "text-secondary-light" : ""}>{t.label}</td>
                  {weeks.map((w, idx) => (
                    <td key={idx} className="text-center">
                      {(w.introductionsByType?.[t.key] ?? 0)} · {(w.revealsByType?.[t.key] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {/* The no-show split (founder 2026-09-11). Half of every
              introduction dealt is answered by exactly one person. The two
              halves need opposite fixes, so they are counted apart: a
              member whose stage surfaced the pair and who still did not
              answer is a MATCHING problem; a member it never reached is a
              REACH problem. Source: stageServedTo, stamped on first
              delivery per member since 2026-09-01. */}
          <h6 className="text-md fw-semibold mt-24 mb-8">Who did not show</h6>
          <p className="text-sm text-secondary-light mb-12">
            Of the introductions dealt each week. One-sided means exactly one
            member answered a Vault question. Split by whether the pair ever
            reached the quiet member&rsquo;s stage.
          </p>
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                <th>Outcome</th>
                {weeks.map((w, idx) => (
                  <th key={idx} className="text-center">
                    {idx === 0 ? "This week" : fmtRange(w.start, w.end)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { key: "bothAnswered", label: "Both answered", help: "The introduction worked as intended" },
                { key: "oneSidedSawIt", label: "One-sided: they saw it and did not answer", help: "Their stage surfaced the pair. A matching and appeal problem." },
                { key: "oneSidedNeverSawIt", label: "One-sided: it never reached them", help: "The pair never appeared on their stage. A reach problem." },
                { key: "untouched", label: "Neither answered", help: "Nobody opened the Vault for this pair" },
              ].map((r) => (
                <tr key={r.key}>
                  <td title={r.help}>{r.label}</td>
                  {weeks.map((w, idx) => {
                    const s = w.noShowSplit || {};
                    const tot =
                      (s.bothAnswered || 0) + (s.oneSidedSawIt || 0) +
                      (s.oneSidedNeverSawIt || 0) + (s.untouched || 0);
                    const n = s[r.key] || 0;
                    return (
                      <td key={idx} className="text-center">
                        {n}
                        {tot > 0 && (
                          <span className="text-secondary-light text-sm">
                            {" "}({Math.round((n / tot) * 100)}%)
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AnalyticsSection>
  );
};

export default LoopFunnelStats;
